import type { RtuFormerWell, RtuRecordState, RtuWell, RtuWellServedBy } from '../../types/RTU';

/** Minimal structural shape the helpers need; the full RTU type satisfies it. */
export interface ClusterRow {
  id: number;
  name?: string | null;
  serialNo?: string | null;
  wells?: RtuWell[] | null;
  formerWells?: RtuFormerWell[] | null;
  clusterParentId?: number | null;
  assetId?: number | null;
  recordState?: RtuRecordState | null;
}

export interface ClusterInfo {
  /** 0 = ordinary row or master, 1 = child shown under a present master. */
  depth: 0 | 1;
  /** Number of children attached to this row (only masters have > 0). */
  childCount: number;
  /** Master id for a child, otherwise null. */
  masterId: number | null;
  /** True for the last child under its master (draws the closing tree connector). */
  isLastChild: boolean;
}

export interface ClusterOrdering<T extends ClusterRow> {
  rows: T[];
  info: Record<number, ClusterInfo>;
}

/**
 * Places each child immediately after its master. A child whose master is absent from `rows`
 * (hidden, detached, filtered) stays a standalone row at its normal position. Depth is 1 only:
 * a "master" that is itself a child (chains, cycles) is not a valid master, so the deeper row stays
 * standalone. Every row appears exactly once.
 */
export const orderWithClusters = <T extends ClusterRow>(rows: T[]): ClusterOrdering<T> => {
  const byId = new Map<number, T>();
  rows.forEach((row) => {
    if (!byId.has(row.id)) {
      byId.set(row.id, row);
    }
  });

  const parentOf = (row: T): T | undefined => {
    const parentId = row.clusterParentId;
    if (parentId === null || parentId === undefined || parentId === row.id) {
      return undefined;
    }
    return byId.get(parentId);
  };
  // A valid master is present and not itself attached to another present row.
  const isRoot = (row: T) => parentOf(row) === undefined;

  const childrenByMaster = new Map<number, T[]>();
  const attached = new Set<T>();
  rows.forEach((row) => {
    const parent = parentOf(row);
    if (parent && byId.get(row.id) === row && isRoot(parent)) {
      const list = childrenByMaster.get(parent.id) ?? [];
      list.push(row);
      childrenByMaster.set(parent.id, list);
      attached.add(row);
    }
  });

  const ordered: T[] = [];
  const info: Record<number, ClusterInfo> = {};
  rows.forEach((row) => {
    if (attached.has(row)) {
      return;
    }
    const children = childrenByMaster.get(row.id) ?? [];
    if (info[row.id] === undefined) {
      info[row.id] = { depth: 0, childCount: children.length, masterId: null, isLastChild: false };
    }
    ordered.push(row);
    children.forEach((child, index) => {
      info[child.id] = { depth: 1, childCount: 0, masterId: row.id, isLastChild: index === children.length - 1 };
      ordered.push(child);
    });
  });
  return { rows: ordered, info };
};

// ---------------------------------------------------------------------------------------------------------------
// Wells: record well vs declared primary
// ---------------------------------------------------------------------------------------------------------------

/**
 * primary   = the declared PRIMARY of the latest well list;
 * record    = the row's own record well (row id/name), shown when no list was reported and listed as "Report record"
 *             when the declared primary is a different well;
 * secondary = any other well of the list.
 */
export type WellKind = 'primary' | 'record' | 'secondary';

export interface WellEntry {
  kind: WellKind;
  wellId: string;
  name: string | null;
  servedBy?: RtuWellServedBy;
  childRtuId?: number | null;
}

export interface WellSource {
  id?: number;
  name?: string | null;
  wells?: RtuWell[] | null;
  formerWells?: RtuFormerWell[] | null;
}

export interface WellView {
  /** The well shown in the row's well name / well ID cells. Never empty for a row that has an id. */
  shown: WellEntry | undefined;
  /** Every other well to list in the popover (the record well first when it differs from the declared primary). */
  extras: WellEntry[];
  /** shown + extras, in popover order. */
  all: WellEntry[];
}

export const formatWell = (well: Pick<WellEntry, 'name' | 'wellId'>): string =>
  well.name ? `${well.name} [${well.wellId}]` : `[${well.wellId}]`;

const recordEntry = (row: WellSource): WellEntry | undefined =>
  row.id === undefined || row.id === null
    ? undefined
    : { kind: 'record', wellId: String(row.id), name: row.name ? row.name : null };

export const getWellView = (row: WellSource): WellView => {
  const list = Array.isArray(row.wells) ? row.wells : [];
  const record = recordEntry(row);
  const primary = list.find((well) => well.role === 'PRIMARY');
  const toEntry = (well: RtuWell, kind: WellKind, fallbackName: string | null = null): WellEntry => ({
    kind,
    wellId: String(well.wellId),
    name: well.name ?? fallbackName,
    servedBy: well.servedBy,
    childRtuId: well.childRtuId ?? null,
  });

  const shown: WellEntry | undefined = primary
    ? toEntry(primary, 'primary', record && record.wellId === String(primary.wellId) ? record.name : null)
    : record;
  const secondaries = list
    .filter((well) => well !== primary)
    .map((well) => toEntry(well, 'secondary'))
    .filter((entry) => !shown || entry.wellId !== shown.wellId);

  const extras: WellEntry[] = [];
  if (primary && record && record.wellId !== shown?.wellId && !secondaries.some((entry) => entry.wellId === record.wellId)) {
    extras.push(record);
  }
  extras.push(...secondaries);
  return { shown, extras, all: shown ? [shown, ...extras] : extras };
};

const entryText = (entry: Pick<WellEntry, 'name' | 'wellId'>): string[] => [entry.name ?? '', entry.wellId].filter(Boolean);

export interface WellSearchOptions {
  /** Include the readable former well records of the device ("Include former wells"). */
  includeFormer?: boolean;
}

const searchedEntries = (row: WellSource, options: WellSearchOptions): Array<{ name: string | null; wellId: string }> => {
  const entries: Array<{ name: string | null; wellId: string }> = [];
  const record = recordEntry(row);
  if (record) {
    entries.push(record);
  }
  (Array.isArray(row.wells) ? row.wells : []).forEach((well) => entries.push({ name: well.name, wellId: String(well.wellId) }));
  if (options.includeFormer) {
    (Array.isArray(row.formerWells) ? row.formerWells : []).forEach((well) =>
      entries.push({ name: well.name, wellId: String(well.wellId) })
    );
  }
  return entries;
};

/**
 * All well names and ids the global search covers: the record well (id + name), every well of the latest list and,
 * only with "Include former wells", the readable former well records.
 */
export const wellSearchText = (row: WellSource, options: WellSearchOptions = {}): string =>
  searchedEntries(row, options).flatMap(entryText).join(' ');

/** Derived string fields for the well column filters: a column filter matches ANY well covered by the search. */
export const wellColumnText = (row: WellSource, options: WellSearchOptions = {}) => {
  const entries = searchedEntries(row, options);
  const unique = (values: string[]) => values.filter((value, index) => value !== '' && values.indexOf(value) === index);
  return {
    wellName: unique(entries.map((entry) => entry.name ?? '')).join(' | '),
    wellId: unique(entries.map((entry) => entry.wellId)).join(' | '),
    __wellSearch: wellSearchText(row, options),
  };
};

const SEARCH_FIELDS = [
  'id',
  'name',
  'serialNo',
  'location',
  'status',
  'statusSource',
  'statusReason',
  'configVersion',
  'deploymentVersion',
  'publicIPAddress',
  'localIPAddress',
  'modemIP',
  'ethernetIP',
  'provider',
  'field',
  'groupId',
] as const;

/** Lowercased text the global search matches against: the asset fields, the serial number and all searched wells. */
export const buildRowSearchText = (
  row: WellSource & { serialNo?: string | null } & Partial<Record<(typeof SEARCH_FIELDS)[number], unknown>>,
  options: WellSearchOptions = {}
): string =>
  [...SEARCH_FIELDS.map((field) => row[field]), wellSearchText(row, options)]
    .filter((value) => value !== undefined && value !== null && value !== '')
    .join(' ')
    .toLowerCase();

const containsQuery = (value: string | null | undefined, needle: string) => String(value ?? '').toLowerCase().includes(needle);
const entryMatches = (entry: Pick<WellEntry, 'name' | 'wellId'>, needle: string) =>
  containsQuery(entry.wellId, needle) || containsQuery(entry.name, needle);

/**
 * The other well a search is matching: when the query matches one of the RTU's other wells but not the row's reference
 * well. The row keeps showing its reference well; the match is only highlighted in the "+N" wells popover.
 */
export const matchingOtherWell = (row: WellSource, query: string | null | undefined): WellEntry | undefined => {
  const needle = String(query ?? '').trim().toLowerCase();
  if (!needle) {
    return undefined;
  }
  const { shown, extras } = getWellView(row);
  if (shown && entryMatches(shown, needle)) {
    return undefined;
  }
  return extras.find((entry) => entryMatches(entry, needle));
};

export const hasAnyWells = (rows: Array<{ wells?: RtuWell[] | null }>): boolean =>
  rows.some((row) => Array.isArray(row.wells) && row.wells.length > 0);

export const hasAnyFormerWells = (rows: Array<{ formerWells?: RtuFormerWell[] | null }>): boolean =>
  rows.some((row) => Array.isArray(row.formerWells) && row.formerWells.length > 0);

// ---------------------------------------------------------------------------------------------------------------
// Served by
// ---------------------------------------------------------------------------------------------------------------

export type ServedByView =
  | { kind: 'this' }
  | { kind: 'child'; name: string }
  /** A child serves the well but is not shown by name: hidden child and unidentified child are indistinguishable. */
  | { kind: 'childAnonymous' }
  | { kind: 'notReported' };

/**
 * THIS_RTU -> this RTU; CHILD_RTU with a childRtuId that resolves to a present row -> that RTU's name; any other
 * CHILD_RTU -> neutral "Child RTU"; UNKNOWN or missing evidence -> not reported (never defaulted to "This RTU").
 */
export const resolveServedBy = (
  well: { servedBy?: RtuWellServedBy | null; childRtuId?: number | null },
  resolveRtu: (id: number) => { name: string } | undefined
): ServedByView => {
  if (well.servedBy === 'THIS_RTU') {
    return { kind: 'this' };
  }
  if (well.servedBy === 'CHILD_RTU') {
    const child = well.childRtuId !== null && well.childRtuId !== undefined ? resolveRtu(well.childRtuId) : undefined;
    return child && child.name ? { kind: 'child', name: child.name } : { kind: 'childAnonymous' };
  }
  return { kind: 'notReported' };
};

/** The "Served by" column only appears when at least one listed well carries real served-by evidence. */
export const hasServedByEvidence = (entries: Array<{ servedBy?: RtuWellServedBy | null }>): boolean =>
  entries.some((entry) => entry.servedBy === 'THIS_RTU' || entry.servedBy === 'CHILD_RTU');

// ---------------------------------------------------------------------------------------------------------------
// Fleet rows and counts
// ---------------------------------------------------------------------------------------------------------------

type FleetRow = { id?: number; assetId?: number | null; recordState?: RtuRecordState | null };

const hasAssetId = (row: FleetRow) => row.assetId !== null && row.assetId !== undefined;

/** Rows displayed: every row except FORMER rows whose asset has a CURRENT row (the device is shown once). */
export const displayedRows = <T extends FleetRow>(rows: T[]): T[] => {
  const currentAssets = new Set<number>();
  rows.forEach((row) => {
    if (row.recordState === 'CURRENT' && hasAssetId(row)) {
      currentAssets.add(row.assetId as number);
    }
  });
  return rows.filter((row) => !(row.recordState === 'FORMER' && hasAssetId(row) && currentAssets.has(row.assetId as number)));
};

export interface FleetCounts {
  /** Distinct assets of CURRENT rows. */
  physical: number;
  /** UNRESOLVED rows: ambiguous identity, each record stands alone. */
  unresolved: number;
  /** Rows with no usable identity (null recordState). */
  unidentified: number;
  /** FORMER rows that are displayed as standalone "former well records" (no CURRENT row to merge into). */
  formerRecords: number;
}

export const fleetCounts = (rows: FleetRow[]): FleetCounts => {
  const currentAssets = new Set<number>();
  rows.forEach((row) => {
    if (row.recordState === 'CURRENT' && hasAssetId(row)) {
      currentAssets.add(row.assetId as number);
    }
  });
  // A CURRENT row without an asset id still stands for one physical device.
  const currentWithoutAsset = rows.filter((row) => row.recordState === 'CURRENT' && !hasAssetId(row)).length;
  return {
    physical: currentAssets.size + currentWithoutAsset,
    unresolved: rows.filter((row) => row.recordState === 'UNRESOLVED').length,
    unidentified: rows.filter((row) => row.recordState === null || row.recordState === undefined).length,
    formerRecords: rows.filter(
      (row) => row.recordState === 'FORMER' && !(hasAssetId(row) && currentAssets.has(row.assetId as number))
    ).length,
  };
};

/** Total RTUs of the fleet: physical + unresolved + unidentified. Former records are history, not devices. */
export const fleetTotal = (counts: FleetCounts): number => counts.physical + counts.unresolved + counts.unidentified;

/** Rows that count toward the fleet total (everything displayed except standalone former records). */
export const fleetRows = <T extends FleetRow>(rows: T[]): T[] =>
  displayedRows(rows).filter((row) => row.recordState !== 'FORMER');
